// src/pages/Guide.js
/* eslint-disable */
import React, { useState } from "react";
import HelmetTemplate from "../utils/HelmetTemplate";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import WebpageShell from "./WebpageShell";

// Images
import img1 from "../images/heroImage1.webp";
import img2 from "../images/heroImage2.webp";
import img3 from "../images/heroImage3.webp";
import img4 from "../images/heroImage4.webp";

export default function Guide() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const images = [img1, img2, img3, img4];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setMessage("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "email_list"), { email });
      setMessage("Thank you, we sent the guide to your e-mail!");
      setEmail("");
    } catch {
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <WebpageShell
      navProps={{ addAnnouncementBar: false, isRecPrototypePage: false }}
      showCTA={true}
      showFooter={true}
    >
      <HelmetTemplate
        title="The Guide - Match Medic"
        description="Stay informed with Match Medic's expert guide to hair transplants. Enter your email to be notified when it's live."
        canonicalUrl="https://matchmedic.com/guide"
      />

      {/* Page container (consistent with other pages) */}
      <div className="w-full md:w-4/5 mx-auto py-10">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8">

          {/* Left — Images */}
          <div className="w-full md:w-3/5">
            {/* Mobile: horizontal swipe row */}
            <div className="block lg:hidden overflow-x-auto whitespace-nowrap space-x-4 flex pb-2">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Guide visual ${idx + 1}`}
                  className="inline-block w-64 rounded-xl flex-shrink-0 cursor-pointer transition-transform duration-200 active:scale-95"
                  onClick={() => {
                    setCurrentIndex(idx);
                    setIsModalOpen(true);
                  }}
                />
              ))}
            </div>

            {/* Desktop: 2x2 grid */}
            <div className="hidden lg:grid grid-cols-2 gap-2 max-w-[36rem] mx-auto">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="aspect-square overflow-hidden rounded-md cursor-pointer"
                  onClick={() => {
                    setCurrentIndex(idx);
                    setIsModalOpen(true);
                  }}
                >
                  <img
                    src={img}
                    alt={`Guide visual ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right — Email Collection */}
          <div className="w-full max-w-md lg:w-1/2 bg-white px-4 md:p-8 rounded-lg">
            <h2 className="text-4xl font-black text-center text-gray-800 mb-4 border-b-2 pb-4">
              Get Our Free Hair Transplant Guide
            </h2>

            {/* Feature Highlights */}
            <div className="grid grid-cols-2 gap-3 mb-6 p-1">
              {[
                "Clinic Red Flags",
                "Procedure Costs",
                "Recovery Tips",
                "Good Surgeons",
                "Tips & Tricks",
                "Treatments",
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="flex bg-blue-50 border border-gray-200 rounded-2xl items-center p-1 w-full"
                >
                  <svg
                    className="w-4 h-auto flex-shrink-0 text-gray-900 mx-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-xs text-gray-700">{feature}</p>
                </div>
              ))}
            </div>

            {/* Email Form */}
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Enter your email to receive our comprehensive hair transplant
                  guide.
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full md:text-sm px-4 py-2 border rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-mm_blue"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <button
                type="submit"
                className={`w-full py-2 px-4 rounded-lg text-white font-bold ${
                  loading ? "bg-gray-500" : "bg-gray-900"
                }`}
                disabled={loading}
              >
                {loading ? "Sending..." : "Get the Guide →"}
              </button>
            </form>

            {message && (
              <div
                className={`mt-4 text-center text-sm ${
                  message.includes("Thank you") ? "text-green-600" : "text-red-600"
                }`}
              >
                {message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Lightbox */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setIsModalOpen(false)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex((currentIndex - 1 + images.length) % images.length);
            }}
            className="absolute left-4 md:left-8 text-white text-4xl font-bold"
            aria-label="Previous image"
          >
            ‹
          </button>

          <img
            src={images[currentIndex]}
            alt={`Image ${currentIndex + 1}`}
            className="max-w-[90%] max-h-[90%] rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex((currentIndex + 1) % images.length);
            }}
            className="absolute right-4 md:right-8 text-white text-4xl font-bold"
            aria-label="Next image"
          >
            ›
          </button>

          <button
            className="absolute top-4 right-4 text-white text-3xl"
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(false);
            }}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>
      )}
    </WebpageShell>
  );
}
